import React from 'react';
import { Provider } from 'react-redux';
import { legacyTheme, ThemeProvider } from '@bettercomp/ui-components';
import Head from 'next/head';
import { AppProps } from "next/app";

import dayjs from 'dayjs'
import advancedFormat from 'dayjs/plugin/advancedFormat'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import localeData from 'dayjs/plugin/localeData'
import weekday from 'dayjs/plugin/weekday'
import weekOfYear from 'dayjs/plugin/weekOfYear'
import weekYear from 'dayjs/plugin/weekYear'

import { App } from 'components/App';
import BaseLayout from 'components/Layout/BaseLayout';
import { JDXpertSettingsProvider } from 'components/UserSettings/JDxpert/JDXpertSettingsContext';
import { AppConfigProvider } from 'components/AppConfigProvider';
import { Pendo } from 'components/ProductAnalysis/Pendo';
import { FeatureFlagProvider } from 'components/FeatureFlagProvider';
import { anonymousRoutes } from 'common/constants';

import { configureStore } from 'redux/configureStore';
import '@bettercomp/ui-components/dist/style.css';
import useRouterScroll from 'hooks/useRouterScroll';
import { IdleWarning } from 'components/IdleWarning';
import './variable.scss';
import './global.scss';

// Issue https://github.com/react-component/picker/issues/123
dayjs.extend(customParseFormat)
dayjs.extend(advancedFormat)
dayjs.extend(weekday)
dayjs.extend(localeData)
dayjs.extend(weekOfYear)
dayjs.extend(weekYear)

const store = configureStore();

const CustomApp = ({ Component, pageProps, router }: AppProps) => {
  useRouterScroll();

  return (
    <div>
      <Head>
        <title>Bettercomp</title>
        <meta charSet='utf-8' />
        <link rel='shortcut icon' href='/favicon.png' />
        <meta httpEquiv='Cache-Control' content='no-cache, no-store, must-revalidate' />
        <meta httpEquiv='Pragma' content='no-cache' />
        <meta httpEquiv='Expires' content='0' />
        <meta name='viewport' content='width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, shrink-to-fit=no' />
        <meta name='theme-color' content='#000000' />
        <meta name='description' content='Bettercomp' />
      </Head>
      <Provider store={store}>
        <FeatureFlagProvider>
          <ThemeProvider themeOverrides={legacyTheme}>
            <BaseLayout>
              <AppConfigProvider>
                <>
                  <Pendo/>
                  {anonymousRoutes.includes(router.pathname)
                    ? (
                      <Component {...pageProps} />
                    )
                    : (
                      <App>
                        <JDXpertSettingsProvider>
                          <Component {...pageProps} />
                          <IdleWarning/>
                        </JDXpertSettingsProvider>
                      </App>
                    )
                  }
                </>
              </AppConfigProvider>
            </BaseLayout>
          </ThemeProvider>
        </FeatureFlagProvider>
      </Provider>
    </div>
  );
};

export default CustomApp;
